import { Controller } from '@hotwired/stimulus'
import { enter, leave } from 'el-transition'

export default class extends Controller {
  static targets = ['overlay', 'panel', 'search', 'list', 'noResult', 'modes']
  static values  = { url: String }

  static selectClasses       = ['bg-gray-400', 'text-white', 'font-semibold']
  static modeDisabledClasses = ['border-gray-400', 'text-gray-900']
  static modeEnabledClasses  = ['border-primary', 'text-primary']

  connect() {
    const template = this.listTarget.querySelector('template')
    this.template = template.content.cloneNode(true)
    template.remove()

    this.opened = !this.element.classList.contains('hidden')

    document.addEventListener('keydown', (event) => {
      if (this.opened && event.key === 'Escape')
        this.hide()
      if (event.ctrlKey && (event.key === 'p' || (event.altKey && ['u','c','s'].includes(event.key)))) {
        event.preventDefault()
        if (event.altKey) this.selectMode(event.key)
        this.toggle()
      }
    })
  }

  toggle() {
    if (this.opened)
      this.hide()
    else
      this.show()
    return false
  }

  show() {
    this.reset()
    this.element.classList.remove('hidden')
    enter(this.overlayTarget)
    enter(this.panelTarget).then(() => {
      this.opened = true
      this.searchTarget.focus()
    })
  }

  hide(callback) {
    leave(this.overlayTarget)
    leave(this.panelTarget).then(() => {
      this.opened = false
      this.element.classList.add('hidden')
      this.reset()
      if (callback instanceof Function) callback()
    })
  }

  control(event) {
    switch (event.key) {
      case 'Enter':
        this.loadSelected(event.shiftKey)
        break
      case 'ArrowUp':
        this.selectPrevious()
        break
      case 'ArrowDown':
        this.selectNext()
        break
      case 'Tab':
        this.cycleMode()
        break
      default:
        if (!event.altKey) break
        this.selectMode(event.key)
    }
  }

  checkSearch(event) {
    if (!event.key.match(/^[a-zA-Z\u00C0-\u017F0-9_\-=@\.]{1}$/) &&
        !['Backspace', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Delete'].includes(event.key))
      event.preventDefault()
  }

  search(event) {
    if ((event.ctrlKey && event.key != 'v')|| event.altKey) return
    if (!event.key.match(/^[a-zA-Z\u00C0-\u017F0-9_\-=@\.]{1}$/) && !['Backspace','Delete'].includes(event.key)) return

    if (this.searchTarget.value === '') {
      this.cleanList()
      return
    }

    const url = new URL(this.urlValue)
    const searchParams = new URLSearchParams()
    searchParams.set('search', this.searchTarget.value)
    url.search = searchParams.toString()
    fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
      .then(response => response.json())
      .then(data => {
        this.cleanList()
        if (data.length <= 0) {
          this.noResultTarget.classList.remove('hidden')
          return
        }

        this.listTarget.classList.remove('hidden')
        let df
        data.forEach(obj => {
          df = this.template.cloneNode(true)
          df.querySelector('[data-username]').innerText = obj.username
          const img = df.querySelector('img')
          if (obj.avatar_url) {
            img.src = obj.avatar_url
            if (obj.user_type_id)
              img.classList.add(`border_type_${obj.user_type_id}`)
          } else {
            img.remove()
          }
          df.firstElementChild.dataset.url = obj.url
          this.listTarget.appendChild(df)
        })

        this.listTarget.scrollTop = 0
      })
  }

  load(event) {
    this.select(event.currentTarget)
    this.loadSelected(event.shiftKey)
  }

  loadSelected(newTab = false) {
    const url = this.selected()?.dataset?.url
    if (!url) return

    this.hide(() => {
      if (newTab)
        window.open(url, '_blank').focus()
      else
        Turbo.visit(url)
    })
  }

  selectPrevious() {
    const previous = this.selected()?.previousElementSibling
    if (!previous) return

    this.select(previous)

    if (previous.getBoundingClientRect().top < this.listTarget.getBoundingClientRect().top)
      previous.scrollIntoView({ block: 'start' })
  }

  selectNext() {
    const next = this.selected() ? this.selected().nextElementSibling : this.listTarget.firstElementChild
    if (!next) return

    this.select(next)

    if (next.getBoundingClientRect().bottom > this.listTarget.getBoundingClientRect().bottom)
      next.scrollIntoView({ block: 'end' })
  }

  select(item) {
    this.unselect()
    item.classList.add(...this.constructor.selectClasses)
    item.dataset.selected = true
  }

  selected() {
    return this.listTarget.querySelector('[data-selected]')
  }

  unselect() {
    const selected = this.selected()
    if (!selected) return
    selected.classList.remove(...this.constructor.selectClasses)
    delete selected.dataset.selected
  }

  reset(){
    this.searchTarget.value = ''
    this.cleanList()
  }

  cleanList(){
    this.listTarget.classList.add('hidden')
    this.noResultTarget.classList.add('hidden')
    this.listTarget.innerHTML = ''
  }

  selectMode(key) {
    switch (key) {
      case 'u':
        this.changeMode('users')
        break
      case 'c':
        this.changeMode('clubs')
        break
      case 's':
        this.changeMode('companies')
        break
      case 'p':
        this.changeMode('pages')
        break
    }
  }

  cycleMode(){
    const modeEls = this.modesTarget.querySelectorAll('[data-mode]')
    const selectedIdx = Array.from(modeEls).findIndex(e => { return e.dataset.selected !== undefined })
    this.changeMode(modeEls[(selectedIdx + 1) % 4].dataset.mode)
  }

  changeMode(mode) {
    const currentEl = this.modesTarget.querySelector('[data-selected]')
    currentEl.classList.remove(...this.constructor.modeEnabledClasses)
    currentEl.classList.add(...this.constructor.modeDisabledClasses)
    delete currentEl.dataset.selected

    const el = this.modesTarget.querySelector((`[data-mode="${mode}"]`))
    el.classList.remove(...this.constructor.modeDisabledClasses)
    el.classList.add(...this.constructor.modeEnabledClasses)
    el.dataset.selected = true

    this.urlValue = el.dataset.url
    this.reset()
  }
}
